/**
 * Native cookie helper (replaces the Vue-2-only `vue-cookies` plugin).
 *
 * Keeps the exact static API the app already calls:
 *   AppCookie.set(name, value, expireTimes, path, domain, secure, sameSite)
 *   AppCookie.get(name)
 *   AppCookie.keyExist(name)
 *   AppCookie.keys()
 *   AppCookie.remove(name)
 *   AppCookie.config(expireTimes, path, domain, secure, sameSite)
 *
 * `expireTimes` accepts the same shorthand as vue-cookies:
 *   'y' year, 'm' month, 'd' day, 'h' hour, 'min' minute, 's' second
 *   (e.g. '1d', '30min'), a number of seconds, a Date, or 'Infinity'/'0'.
 *
 * expireTimes String format
 *
 * Unit	full name
 * y	year
 * m	month
 * d	day
 * h	hour
 * min	minute
 * s	second
 *
 * example: '1d'
 */

const defaults = {
    expires: '1d',
    path: '/',
    domain: '',
    secure: '',
    sameSite: '',
};

function toExpires(expireTimes) {
    if (expireTimes === undefined || expireTimes === null || expireTimes === '') {
        expireTimes = defaults.expires;
    }

    // Never-expiring cookie.
    if (expireTimes === Infinity || expireTimes === 'Infinity') {
        return 'expires=Fri, 31 Dec 9999 23:59:59 GMT';
    }

    // Explicit Date.
    if (expireTimes instanceof Date) {
        return 'expires=' + expireTimes.toUTCString();
    }

    // Plain number → seconds from now.
    if (typeof expireTimes === 'number') {
        const d = new Date();
        d.setTime(d.getTime() + expireTimes * 1000);
        return 'expires=' + d.toUTCString();
    }

    // Shorthand like '1d', '30min', '2h', '1y', '6m', '45s'.
    const match = String(expireTimes).match(/^([\d.]+)(y|m|d|h|min|s)$/);
    if (match) {
        const amount = parseFloat(match[1]);
        const unit = match[2];
        const d = new Date();
        switch (unit) {
            case 'y': d.setFullYear(d.getFullYear() + amount); break;
            case 'm': d.setMonth(d.getMonth() + amount); break;
            case 'd': d.setDate(d.getDate() + amount); break;
            case 'h': d.setHours(d.getHours() + amount); break;
            case 'min': d.setMinutes(d.getMinutes() + amount); break;
            case 's': d.setSeconds(d.getSeconds() + amount); break;
            default: break;
        }
        return 'expires=' + d.toUTCString();
    }

    // Anything else (e.g. a raw GMT string) is passed through.
    return 'expires=' + expireTimes;
}

export default class AppCookie {
    static config(expireTimes, path, domain, secure, sameSite) {
        if (expireTimes !== undefined) defaults.expires = expireTimes;
        if (path !== undefined) defaults.path = path;
        if (domain !== undefined) defaults.domain = domain;
        if (secure !== undefined) defaults.secure = secure;
        if (sameSite !== undefined) defaults.sameSite = sameSite;
        return this;
    }

    static set(keyName, value, expireTimes = defaults.expires, path = defaults.path, domain = defaults.domain, secure = defaults.secure, sameSite = defaults.sameSite) {
        let cookie = encodeURIComponent(keyName) + '=' + encodeURIComponent(value) + '; ' + toExpires(expireTimes);
        if (path) cookie += '; path=' + path;
        if (domain) cookie += '; domain=' + domain;
        if (secure) cookie += '; secure';
        if (sameSite) cookie += '; SameSite=' + sameSite;
        document.cookie = cookie;
        return this;
    }

    static get(keyName) {
        const name = encodeURIComponent(keyName);
        const cookies = document.cookie ? document.cookie.split('; ') : [];
        for (let i = 0; i < cookies.length; i++) {
            const idx = cookies[i].indexOf('=');
            const k = cookies[i].substring(0, idx);
            if (k === name) {
                return decodeURIComponent(cookies[i].substring(idx + 1));
            }
        }
        return null;
    }

    static keyExist(keyName) {
        return this.get(keyName) !== null;
    }

    static keys() {
        if (!document.cookie) return [];
        return document.cookie.split('; ').map((c) => decodeURIComponent(c.substring(0, c.indexOf('='))));
    }

    static remove(keyName, path = defaults.path, domain = defaults.domain) {
        if (!this.keyExist(keyName)) return false;
        let cookie = encodeURIComponent(keyName) + '=; expires=Thu, 01 Jan 1970 00:00:00 GMT';
        if (path) cookie += '; path=' + path;
        if (domain) cookie += '; domain=' + domain;
        document.cookie = cookie;
        return true;
    }
}
